function apiBase() {
  const configured = import.meta.env.VITE_API_URL || "http://localhost:4000";
  if (typeof window === "undefined") return configured;
  try {
    const api = new URL(configured);
    const pageHost = window.location.hostname;
    const apiIsLocal = api.hostname === "localhost" || api.hostname === "127.0.0.1";
    const pageIsLocal = pageHost === "localhost" || pageHost === "127.0.0.1";
    if (apiIsLocal && !pageIsLocal) {
      api.hostname = pageHost;
      return api.origin;
    }
  } catch {
    return configured;
  }
  return configured;
}

export type AuthUser = {
  id: string;
  fullName: string;
  phone: string;
  email: string | null;
  storeId?: string | null;
  shopName?: string | null;
  welcomeBannerDismissed?: boolean;
  role?: "CUSTOMER" | "ADMIN";
  kycSubmitted?: boolean;
  kycStatus?: string;
  kycTier?: string;
  wallet?: { id: string; publicNumber: string; storeId: string } | null;
};

export type OtpChallenge = {
  challengeId: string;
  purpose: "LOGIN" | "SIGNUP";
  expiresAt: string;
  channels: { sms: boolean; email: boolean };
  destinations: { phone: string | null; email: string | null };
};

type ApiResult<T> = T & { ok: boolean; error?: string; message?: string };

function deviceId() {
  if (typeof window === "undefined") return "";
  try {
    let id = window.localStorage.getItem("jazia_device");
    if (!id) {
      id = typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem("jazia_device", id);
    }
    return id;
  } catch {
    return "";
  }
}

const RETRY_DELAYS_MS = [800, 1600, 2400];

async function reach(url: string, init: RequestInit) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await fetch(url, init);
    } catch (error) {
      // fetch rejects with a TypeError only when no response arrived (server down or restarting).
      if (!(error instanceof TypeError) || attempt >= RETRY_DELAYS_MS.length) {
        throw new Error("Could not reach the Jazia server. Check your connection and try again.");
      }
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
    }
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<ApiResult<T>> {
  const device = deviceId();
  const response = await reach(`${apiBase()}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(device ? { "X-Jazia-Device": device } : {}),
      ...(init?.headers || {}),
    },
  });

  let data: ApiResult<T>;
  try {
    data = (await response.json()) as ApiResult<T>;
  } catch {
    throw new Error("Could not reach the Jazia server. Is the backend running?");
  }

  if (!response.ok || data.ok === false) {
    const err = new Error(data.error || "Request failed.") as Error & {
      status?: number;
      existingItemId?: string;
      code?: string;
    };
    err.status = response.status;
    err.existingItemId = (data as { existingItemId?: string }).existingItemId;
    err.code = (data as { code?: string }).code;
    const extra = err as Error & { toastTitle?: string; available?: number; skuId?: string; fields?: Array<[string, string]> };
    extra.fields = (data as { fields?: Array<[string, string]> }).fields;
    extra.toastTitle = (data as { toastTitle?: string }).toastTitle;
    extra.available = (data as { available?: number }).available;
    extra.skuId = (data as { skuId?: string }).skuId;
    throw err;
  }

  return data;
}

export type SignupPayload = {
  fullName: string;
  phone: string;
  email?: string | undefined;
  password: string;
  shopName: string;
  county: string;
  town: string;
  updatesOptIn?: boolean | undefined;
};

export const authApi = {
  signup(body: SignupPayload) {
    return request<{ user: AuthUser; challenge: OtpChallenge; message: string }>("/api/auth/signup", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  login(body: { identity: string; password: string }) {
    return request<{ user: AuthUser; challenge: OtpChallenge; message: string }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  verifyOtp(body: { userId: string; code: string; purpose: "LOGIN" | "SIGNUP" }) {
    return request<{ token: string; user: AuthUser; message: string }>("/api/auth/verify-otp", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  resendOtp(body: { userId: string; purpose: "LOGIN" | "SIGNUP" }) {
    return request<{ user: AuthUser; challenge: OtpChallenge; message: string }>("/api/auth/resend-otp", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  invite(token: string) {
    return request<{ invite: { fullName: string; email: string; role: string; expiresAt: string } }>(
      `/api/auth/invite/${encodeURIComponent(token)}`,
    );
  },

  acceptInvite(token: string, password: string) {
    return request<{ message: string }>(`/api/auth/invite/${encodeURIComponent(token)}`, {
      method: "POST",
      body: JSON.stringify({ password }),
    });
  },
};

async function authRequest<T>(path: string, token: string, init?: RequestInit): Promise<ApiResult<T>> {
  return request<T>(path, {
    ...init,
    headers: {
      ...(init?.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });
}

export type InventoryOutlet = { id: string; shopName: string; location: string };
export type InventoryCategory = { id: string; name: string; prefix: string; itemCount?: number };
export type InventoryProductOption = { id: string; name: string };

export type InventoryItem = {
  id: string;
  type: "PRODUCT" | "SERVICE";
  name: string;
  description?: string | null;
  taxClass: string;
  priceType?: string | null;
  estimatedDurationMinutes?: number | null;
  category: { id: string; name: string; prefix: string };
  skus: Array<{
    id: string;
    code: string;
    barcode?: string | null;
    unit: string;
    sellingPrice: string | number;
    costPrice?: string | number | null;
    reorderLevel?: string | number | null;
    variantLabel?: string | null;
  }>;
};

export type CreateItemPayload = Record<string, unknown>;

export type InventoryListItem = {
  id: string;
  name: string;
  type: "PRODUCT" | "SERVICE";
  category: string;
  sku: string;
  variantCount: number;
  priceLabel: string;
  quantity: number | null;
  unit: string;
  reorderLevel: number;
  low: boolean;
  active: boolean;
};

export const inventoryApi = {
  meta(token: string) {
    return authRequest<{
      outlets: InventoryOutlet[];
      categories: InventoryCategory[];
      products: InventoryProductOption[];
    }>("/api/inventory/meta", token);
  },

  createCategory(token: string, name: string) {
    return authRequest<{ category: InventoryCategory; promptFirstItem: boolean }>("/api/inventory/categories", token, {
      method: "POST",
      body: JSON.stringify({ name }),
    });
  },

  deleteCategory(token: string, categoryId: string) {
    return authRequest<{ ok: true }>(`/api/inventory/categories/${categoryId}`, token, { method: "DELETE" });
  },

  list(token: string) {
    return authRequest<{ items: InventoryListItem[] }>("/api/inventory/items", token);
  },

  setActive(token: string, itemId: string, active: boolean) {
    return authRequest<{ item: { id: string; name: string; active: boolean } }>(`/api/inventory/items/${itemId}/active`, token, {
      method: "POST",
      body: JSON.stringify({ active }),
    });
  },

  createItem(token: string, body: CreateItemPayload) {
    return authRequest<{ item: InventoryItem; message: string }>("/api/inventory/items", token, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  getItem(token: string, itemId: string) {
    return authRequest<{ item: InventoryItem }>(`/api/inventory/items/${itemId}`, token);
  },
};

export type PosterData = {
  shopName: string;
  storeId: string;
  qrSvg: string;
  qrPayload: string | null;
  wallet: { id: string; publicNumber: string } | null;
};

export type ReceiptData = {
  heading: string;
  tone: "ok" | "warn" | "bad";
  amount: number;
  at: string;
  rows: Array<[string, string]>;
};

export type AccountTx = {
  id: string;
  at: string;
  amount: number;
  status: "Completed" | "On hold" | "Processing" | "Reversed";
  verified: boolean;
  type: "Sale payment" | "Payment received" | "Loan received" | "Loan repayment" | "Held payment" | "Withdrawal" | "Withdrawal reversed";
  title: string;
  sub: string;
  ref: string;
  info?: string;
  sale?: { id: string; number: string; token: string; customer: string };
  withdrawal?: { id: string; number: string; code: string; fee: number; destination: string } | null;
  receiptNo: string;
  receipt: ReceiptData | null;
  balanceAfter: number | null;
};

export type WithdrawDestination = { channel: "MPESA" | "BANK"; label: string; name: string; fee: number };

export type StatementPeriod = {
  businessName: string;
  accountNumber: string;
  from: string;
  to: string;
  periodLabel: string;
  opening: number;
  moneyIn: number;
  moneyOut: number;
  closing: number;
  rows: Array<{ id: string; at: string; description: string; reference: string; inAmount: number; outAmount: number; balance: number | null }>;
};

export type WithdrawalResult = {
  id: string;
  number: string;
  amount: number;
  fee: number;
  destination: string;
  transactionCode: string | null;
  receiptNumber: string | null;
  balanceAfter: number;
  at: string;
};

export type AccountStatement = {
  balance: number;
  startBalance: number;
  withdraw: { enabled: boolean; minAmount: number; maxAmount: number; verified: boolean; destinations: WithdrawDestination[] };
  receivedToday: number;
  onHold: number;
  accountNumber: string;
  storeId: string;
  paybill: string;
  businessName: string;
  outlets: Array<{ id: string; name: string; location: string }>;
  payout: { kind: string; label: string; name: string; verified: boolean } | null;
  kycStatus: string;
  loan: { id: string; number: string; product: string; balance: number; total: number; repaid: number; nextAmount: number; nextDue: string | null; overdue: number; method: string; share: number } | null;
  integrityOk: boolean;
  rows: AccountTx[];
};

export type HomeActivity = AccountTx & { display: { dir: "in" | "out"; label: string; detail: string; tag: string } };

export type HomeSummary = {
  firstName: string;
  fullName: string;
  businessName: string;
  wallet: { balance: number; pending: number; monthIn: number; accountNumber: string; storeId: string; paybill: string };
  days: Array<{ date: string; total: number; orders: number; inflow: number }>;
  inflow30: number;
  inflowPrev30: number;
  lowStock: { count: number; items: Array<{ id: string; name: string; quantity: number; reorderLevel: number; unit: string }> };
  loan: {
    state: string;
    limit: number;
    checks: Array<{ ok: boolean; waiting: boolean; title: string; detail: string }>;
    active: { number: string; status: string; balance: number; total: number; repaid: number; nextAmount: number; nextDue: string | null; overdue: number; method: string; share: number } | null;
    pending: unknown;
  } | null;
  activity: HomeActivity[];
};

export const accountApi = {
  wallet(token: string) {
    return authRequest<AccountStatement>("/api/account/wallet", token);
  },
  home(token: string) {
    return authRequest<HomeSummary>("/api/account/home", token);
  },
  me(token: string) {
    return authRequest<{ user: AuthUser }>("/api/account/me", token);
  },
  dismissWelcomeBanner(token: string) {
    return authRequest<{ user: AuthUser }>("/api/account/welcome-banner/dismiss", token, { method: "POST" });
  },
  poster(token: string) {
    return authRequest<{ poster: PosterData }>("/api/account/poster", token);
  },
  startWithdrawal(token: string, body: { channel: string; amount: number }) {
    return authRequest<{
      withdrawal: { id: string; number: string; amount: number; fee: number; total: number; destination: string };
      challenge: { expiresAt: string; destinations: { phone: string | null; email: string | null } };
      message: string;
    }>("/api/account/withdrawals", token, { method: "POST", body: JSON.stringify(body) });
  },
  confirmWithdrawal(token: string, id: string, code: string) {
    return authRequest<{ status: "COMPLETED" | "PROCESSING"; withdrawal: WithdrawalResult; message: string }>(
      `/api/account/withdrawals/${id}/confirm`,
      token,
      { method: "POST", body: JSON.stringify({ code }) },
    );
  },
  resendWithdrawalCode(token: string, id: string) {
    return authRequest<{ message: string }>(`/api/account/withdrawals/${id}/resend`, token, { method: "POST" });
  },
  statement(token: string, from: string, to: string) {
    return authRequest<{ statement: StatementPeriod }>(`/api/account/statement?from=${from}&to=${to}`, token);
  },
  emailStatement(token: string, from: string, to: string) {
    return authRequest<{ emailed: boolean; message: string }>("/api/account/statement/email", token, {
      method: "POST",
      body: JSON.stringify({ from, to }),
    });
  },
  async pdf(token: string, path: string) {
    const response = await fetch(`${apiBase()}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) {
      const data = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new Error(data?.error || "Could not create the PDF.");
    }
    return response.blob();
  },
  receiptPdf(token: string, entryId: string) {
    return accountApi.pdf(token, `/api/account/receipts/${encodeURIComponent(entryId)}/pdf`);
  },
  statementPdf(token: string, from: string, to: string) {
    return accountApi.pdf(token, `/api/account/statement/pdf?from=${from}&to=${to}`);
  },
};

export type ReportRange = "7" | "30" | "90" | "ytd";
export type ReportKind = "sales" | "wallet" | "stock" | "loans" | "tax" | "receipts" | "business" | "bestsellers";
export type ReportFormat = "pdf" | "csv" | "xlsx";
export type ReportKpi = { value: number; change: number | null };
export type ReportSchedule = { key: string; title: string; when: string; on: boolean };

export type ReportSummary = {
  range: ReportRange;
  periodLabel: string;
  businessName: string;
  kpis: { revenue: ReportKpi; orders: ReportKpi; average: ReportKpi; net: ReportKpi };
  moneyIn: number;
  moneyOut: number;
  bars: Array<{ label: string; full: string; in: number; out: number }>;
  channels: Array<{ key: string; label: string; color: string; amount: number; share: number }>;
  channelTotal: number;
  bestSellers: Array<{ id: string; name: string; type: "Product" | "Service"; unit: string; revenue: number; units: number; margin: number | null; growth: number | null }>;
  schedules: ReportSchedule[];
  contact: { email: string; phone: string };
};

export const reportsApi = {
  summary(token: string, range: ReportRange) {
    return authRequest<ReportSummary>(`/api/account/reports?range=${range}`, token);
  },
  schedule(token: string, key: string, on: boolean) {
    return authRequest<{ schedules: ReportSchedule[] }>("/api/account/reports/schedules", token, {
      method: "PUT",
      body: JSON.stringify({ key, on }),
    });
  },
  async download(token: string, kind: ReportKind, range: ReportRange, format: ReportFormat) {
    const response = await fetch(`${apiBase()}/api/account/reports/${kind}/download?range=${range}&format=${format}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const data = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new Error(data?.error || "Could not prepare that report.");
    }
    const disposition = response.headers.get("Content-Disposition") || "";
    const name = /filename="([^"]+)"/.exec(disposition)?.[1] || `Jazia-report.${format}`;
    return { blob: await response.blob(), name };
  },
};

export type StaffRole = "SYSTEM_ADMIN" | "KYC_REVIEWER" | "LOAN_OFFICER" | "COMPLIANCE" | "SUPPORT";
export type StaffPermission = "customers" | "kyc" | "loans" | "payments" | "aml";

export type TeamMember = {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: StaffRole;
  roleLabel: string;
  permissions: StaffPermission[];
  status: "ACTIVE" | "INVITED" | "SUSPENDED";
  lastActiveAt: string | null;
  invitedAt: string;
  inviteExpired: boolean;
  isYou: boolean;
};

export type TeamData = {
  members: TeamMember[];
  roles: Array<{ key: StaffRole; label: string; permissions: StaffPermission[] }>;
  permissions: StaffPermission[];
  counts: { total: number; active: number; invited: number; suspended: number };
};

export type WithdrawalSettings = {
  enabled: boolean;
  minAmount: number;
  maxAmount: number;
  autoLimit: number;
  dailyAutoLimit: number;
  manualThreshold: number;
  feeMpesa: number;
  feeBank: number;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

export type ApprovalRow = {
  id: string;
  number: string;
  at: string;
  type: string;
  business: string;
  owner: string;
  phone: string;
  customerId: string;
  accountNumber: string;
  rating: string;
  amount: number;
  fee: number;
  channel: string;
  destination: string;
  accountName: string;
  why: string;
  risk: number;
  reasons: Array<{ code: string; title: string; detail: string }>;
  need: number;
  approvals: Array<{ by: string; name: string; at: string }>;
  status: "Pending" | "Approved" | "Rejected";
  rawStatus: string;
  rejectReason: string;
  decidedBy: string;
  decidedAt: string | null;
  transactionCode: string;
  receiptNumber: string;
  balanceAfter: number | null;
};

export type AdminCustomerRow = {
  id: string;
  business: string;
  storeId: string;
  owner: string;
  phone: string;
  email: string;
  accountNumber: string;
  kyc: string;
  kycStatus: string;
  kycId: string | null;
  tier: string;
  risk: string;
  why: string[];
  status: "Active" | "Restricted" | "Frozen" | "Blocked";
  balance: number | null;
  held: number;
  loans: string;
  flags: number;
  segment: string;
  since: string;
};

export type AdminCustomerDetail = {
  customer: AdminCustomerRow;
  business: { type: string; regNo: string; kraPin: string; sector: string; address: string };
  kyc: { id: string; status: string; rawStatus: string; tier: string; submittedAt: string | null; reviewedAt: string | null; reviewer: string; note: string } | null;
  restrictions: { withdrawalsRestricted: boolean; payoutsHeld: boolean; frozen: boolean; confirmedFraud: boolean; edd: boolean };
  outlets: Array<{ id: string; name: string; type: string; location: string }>;
  activity: { salesCount: number; salesTotal: number; lastSaleAt: string | null; sales30Count: number; sales30Total: number; withdrawalsCount: number; withdrawnTotal: number };
  credit: { score: number; band: string; label: string; limit: number; state: string } | null;
  loans: Array<{ id: string; number: string; product: string; principal: number; balance: number; status: string; startAt: string; daysPastDue: number }>;
  alerts: Array<{ id: string; number: string; title: string; category: string; severity: string; at: string }>;
  cases: Array<{ id: string; number: string; status: string; priority: string; at: string }>;
  heldFunds: Array<{ id: string; amount: number; source: string; reference: string; at: string }>;
  log: Array<{ at: string; text: string; by: string }>;
};

export type AdminCustomerTransactions = {
  customer: { id: string; business: string; owner: string; storeId: string; accountNumber: string; since: string };
  balance: number | null;
  onHold: number;
  integrityOk: boolean;
  wallet: Array<{ id: string; at: string; type: string; title: string; sub: string; ref: string; amount: number; balanceAfter: number | null; status: string; verified: boolean }>;
  sales: Array<{ id: string; number: string; at: string; outlet: string; customer: string; method: string; code: string; status: string; total: number; paid: number }>;
};

export const adminApi = {
  customers(token: string) {
    return authRequest<{ rows: AdminCustomerRow[] }>("/api/admin/customers", token);
  },
  customer(token: string, id: string) {
    return authRequest<AdminCustomerDetail>(`/api/admin/customers/${id}`, token);
  },
  customerTransactions(token: string, id: string) {
    return authRequest<AdminCustomerTransactions>(`/api/admin/customers/${id}/transactions`, token);
  },
  messageCustomer(token: string, id: string, message: string) {
    return authRequest<{ message: string }>(`/api/admin/customers/${id}/message`, token, { method: "POST", body: JSON.stringify({ message }) });
  },
  team(token: string) {
    return authRequest<TeamData>("/api/admin/team", token);
  },
  invite(token: string, body: { fullName: string; phone: string; email: string; role: StaffRole; permissions: StaffPermission[] }) {
    return authRequest<{ member: TeamMember; emailed: boolean; message: string }>("/api/admin/team", token, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  updateMember(token: string, id: string, body: { role: StaffRole; permissions: StaffPermission[] }) {
    return authRequest<{ member: TeamMember; message: string }>(`/api/admin/team/${id}`, token, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  memberAction(token: string, id: string, action: "resend" | "suspend" | "restore") {
    return authRequest<{ member: TeamMember; message: string }>(`/api/admin/team/${id}/${action}`, token, { method: "POST" });
  },
  withdrawalSettings(token: string) {
    return authRequest<{ settings: WithdrawalSettings }>("/api/admin/settings/withdrawals", token);
  },
  saveWithdrawalSettings(token: string, body: WithdrawalSettings) {
    return authRequest<{ settings: WithdrawalSettings; message: string }>("/api/admin/settings/withdrawals", token, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
  approvals(token: string) {
    return authRequest<{ rows: ApprovalRow[]; counts: { pending: number; pendingValue: number; highRisk: number; oldestHours: number } }>(
      "/api/admin/approvals",
      token,
    );
  },
  approvalCounts(token: string) {
    return authRequest<{ pending: number }>("/api/admin/approvals/counts", token);
  },
  approve(token: string, id: string) {
    return authRequest<{ row: ApprovalRow; message: string }>(`/api/admin/approvals/${id}/approve`, token, { method: "POST" });
  },
  reject(token: string, id: string, reason: string) {
    return authRequest<{ row: ApprovalRow; message: string }>(`/api/admin/approvals/${id}/reject`, token, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
};

export const PAYBILL = String(import.meta.env.VITE_PAYBILL || import.meta.env.PAYBILL || "");

export type KycFile = { name: string; size: number; type: string; url?: string; storedName?: string };

export type KycDraft = {
  role: string;
  name: string;
  idType: string;
  idNo: string;
  dob: string;
  nat: string;
  pin: string;
  phone: string;
  email: string;
  county: string;
  town: string;
  pep: string;
  pepNote: string;
  btype: string;
  bname: string;
  tname: string;
  regno: string;
  regdate: string;
  bpin: string;
  sector: string;
  sectorNote: string;
  bcounty: string;
  btown: string;
  bstreet: string;
  outlets: string;
  turn: string;
  source: string;
  sourceNote: string;
  owners: Array<{ name: string; id: string; role: string; pct: string }>;
  noOthers: boolean;
  payType: string;
  payPhone: string;
  bank: string;
  acct: string;
  payChecked: boolean;
  skipPayout: boolean;
  acctName: string;
  d1: boolean;
  d2: boolean;
  d3: boolean;
  d4: boolean;
  files: Record<string, KycFile | undefined>;
  done: Record<string, boolean | undefined>;
  currentStep?: string;
  capture?: { token?: string; createdAt?: string; expiresAt?: string; completedAt?: string | null };
};

export type KycCaptureShot = { key: string; taken: boolean };

export type KycCaptureSession = {
  idType: string;
  expiresAt: string | null;
  completed: boolean;
  shots: KycCaptureShot[];
};

export type KycState = {
  kycSubmitted: boolean;
  kycStatus: string;
  kycTier: string;
  decisionNote?: string;
  draft: KycDraft;
  storeId: string | null;
  shopName: string | null;
  county?: string;
  town?: string;
  user?: AuthUser;
};

export type KycQueueRow = {
  id: string;
  userId: string;
  business: string;
  owner: string;
  phone: string;
  submittedAt: string | null;
  ageDays: number;
  daysLeft: number | null;
  risk: string;
  flags: string[];
  status: string;
  tier: string;
  reviewerName: string | null;
  decisionNote: string | null;
  draft?: KycDraft;
};

export const kycApi = {
  get(token: string) {
    return authRequest<KycState>("/api/kyc", token);
  },
  save(token: string, draft: KycDraft) {
    return authRequest<KycState>("/api/kyc", token, { method: "PUT", body: JSON.stringify(draft) });
  },
  upload(token: string, body: { key: string; name: string; type: string; data: string }) {
    return authRequest<{ file: KycFile } & KycState>("/api/kyc/file", token, { method: "POST", body: JSON.stringify(body) });
  },
  submit(token: string) {
    return authRequest<KycState>("/api/kyc/submit", token, { method: "POST" });
  },
  openCapture(token: string, pageOrigin: string, force = false) {
    return authRequest<{ token: string; createdAt: string; expiresAt: string; qr: string }>("/api/kyc/capture", token, {
      method: "POST",
      body: JSON.stringify({ force, pageOrigin }),
    });
  },
  capture(token: string) {
    return request<KycCaptureSession>(`/api/kyc/capture/${encodeURIComponent(token)}`);
  },
  captureFile(token: string, body: { key: string; data: string }) {
    return request<{ file: KycFile }>(`/api/kyc/capture/${encodeURIComponent(token)}/file`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  finishCapture(token: string) {
    return request<{ completed: boolean }>(`/api/kyc/capture/${encodeURIComponent(token)}/finish`, { method: "POST" });
  },
  queue(token: string) {
    return authRequest<{ rows: KycQueueRow[] }>("/api/kyc/admin", token);
  },
  review(token: string, id: string) {
    return authRequest<{ file: KycQueueRow }>("/api/kyc/admin/" + id, token);
  },
  async fileBlob(token: string, userId: string, key: string) {
    const response = await fetch(`${apiBase()}/api/kyc/admin/${userId}/files/${key}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error("Could not open that file.");
    return response.blob();
  },
  async openFile(token: string, userId: string, key: string) {
    const blob = await kycApi.fileBlob(token, userId, key);
    window.open(URL.createObjectURL(blob), "_blank", "noopener");
  },
  decide(token: string, id: string, action: "approve" | "reject" | "request-info" | "escalate", reason?: string) {
    return authRequest<{ row: KycQueueRow; notice?: { sms?: { sent?: boolean; reason?: string }; email?: { sent?: boolean; reason?: string } } | null }>(`/api/kyc/admin/${id}/${action}`, token, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
};

export type SaleCatalogItem = {
  skuId: string;
  name: string;
  code: string;
  barcode: string | null;
  unit: string;
  price: number;
  priceType: string | null;
  taxClass: string;
  type: "PRODUCT" | "SERVICE";
  trackStock: boolean;
  available: number | null;
};

export type SaleRecord = {
  id: string;
  status: string;
  outletName: string | null;
  outletLocation: string | null;
  servedBy: string | null;
  shopPhone: string | null;
  receiptNumber: string | null;
  publicToken: string | null;
  createdAt: string;
  customerName: string | null;
  customerPhone: string | null;
  subtotal: number;
  tax: number;
  total: number;
  amountPaid: number;
  balance: number;
  paymentMethod: string | null;
  lines: Array<{ skuId: string; name: string; quantity: number; unitPrice: number; taxClass?: string; lineTotal: number }>;
  payment: {
    method: string;
    status: string;
    failureReason: string | null;
    providerRef: string | null;
    amount?: number;
    amountReceived: number | null;
    changeGiven: number | null;
  } | null;
  verifyUrl?: string;
  qrSvg?: string;
};

export type PurchaseOrderRecord = {
  id: string;
  number: string;
  supplierName: string;
  supplierContact: string;
  expectedOn: string | null;
  deliverTo: string;
  terms: string;
  notes: string;
  chargeVat: boolean;
  status: string;
  createdAt: string;
  subtotal: number;
  vat: number;
  total: number;
  itemCount: number;
  lines: Array<{ id: string; name: string; skuCode: string; quantity: number; receivedQuantity: number; unitCost: number; lineTotal: number }>;
};

export const salesApi = {
  list(token: string) {
    return authRequest<{ sales: SaleRecord[] }>("/api/sales", token);
  },
  catalog(token: string, outletId?: string) {
    const query = outletId ? `?outletId=${encodeURIComponent(outletId)}` : "";
    return authRequest<{ outlets: Array<{ id: string; shopName: string; location: string }>; outletId: string | null; items: SaleCatalogItem[]; mpesa: boolean }>(
      `/api/sales/catalog${query}`,
      token,
    );
  },
  stockCheck(token: string, body: { skuId: string; outletId: string; quantity: number; unitPrice?: number }) {
    return authRequest<{ appliedQty: number; available: number | null; limited: boolean; toastTitle?: string; toastMessage?: string; unitPrice: number; name: string }>(
      "/api/sales/stock-check",
      token,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  reserve(token: string, body: Record<string, unknown>) {
    return authRequest<{ sale: SaleRecord }>("/api/sales/reserve", token, { method: "POST", body: JSON.stringify(body) });
  },
  cancel(token: string, saleId: string) {
    return authRequest<{ sale: SaleRecord }>(`/api/sales/${saleId}/cancel`, token, { method: "POST" });
  },
  payCash(token: string, saleId: string, amountReceived: number) {
    return authRequest<{ sale: SaleRecord }>(`/api/sales/${saleId}/pay/cash`, token, {
      method: "POST",
      body: JSON.stringify({ amountReceived }),
    });
  },
  payMpesaCode(token: string, saleId: string, code: string) {
    return authRequest<{ sale: SaleRecord }>(`/api/sales/${saleId}/pay/mpesa-code`, token, {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },
  payMpesa(token: string, saleId: string, phone: string) {
    return authRequest<{ sale: SaleRecord; phone: string }>(`/api/sales/${saleId}/pay/mpesa`, token, {
      method: "POST",
      body: JSON.stringify({ phone }),
    });
  },
  status(token: string, saleId: string) {
    return authRequest<{ sale: SaleRecord }>(`/api/sales/${saleId}`, token);
  },
};

export function publicReceipt(token: string) {
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  return request<{ receipt: SaleRecord }>(`/api/receipts/${encodeURIComponent(token)}?origin=${encodeURIComponent(origin)}`);
}

export type LoanCheck = { status: "y" | "n" | "w"; title: string; detail: string };
export type LoanProductView = {
  code: string;
  name: string;
  days: number;
  rate: number;
  fee: number;
  desc: string;
  frequencies: string[];
  share: number;
  minAmount: number;
  maxAmount: number;
  lateDailyPercent: number;
  version?: number;
  defaultFrequency?: string;
  lateText?: string;
  graceDays?: number;
  earlyRepayment?: boolean;
  pricing?: { method: string; per: string; unit: string; tdef: number; rate: number; feeT: string; fee: number };
};
export type LoanRestructureOffer = {
  id: string;
  source: string;
  balance: number;
  termDays: number;
  frequency: string;
  payments: number;
  instalment: number;
  due: string;
  addedCost: number;
};
export type LoanView = {
  id: string;
  number: string;
  prod: string;
  pr: number;
  i: number;
  f: number;
  total: number;
  repaid: number;
  balance: number;
  method: string;
  start: string;
  mat: string;
  nextA: number;
  nextD: string;
  over: number;
  dpd: number;
  share: number;
  purpose: string;
  status: string;
  early: boolean;
  due: number;
};
export type LoanOverview = {
  state: "building" | "eligible" | "paused" | "not_eligible" | "active" | "arrears" | "declined" | "review" | "eligible_conditions" | "no_products";
  limit: number;
  minAmount: number;
  wallet: number;
  phoneMasked: string;
  band: string;
  bandLabel: string;
  score: number;
  eligibility: { checks: LoanCheck[]; progress: number; days: number; daysRequired: number; daysLeft: number };
  products: LoanProductView[];
  pending: { ref: string; amt: number; status: string } | null;
  loan: LoanView | null;
  activity: LoanActivityRow[];
  restructureOffer: LoanRestructureOffer | null;
  restructureStatus: "PENDING" | "OFFERED" | null;
  history: Array<{ id: string; number: string; amount: string; product: string; date: string; outcome: string }>;
  decline: { reasons: LoanCheck[]; applyAgainAt: string | null } | null;
  purchaseOrders: Array<{ id: string; number: string; label: string }>;
  lenderName: string;
  supportEmail: string;
  supportPhone: string;
  share: number;
};

export type LoanActivityRow = {
  id: string;
  type: string;
  title: string;
  note: string;
  amount: number;
  direction: "in" | "out" | "none";
  at: string;
  balanceAfter: number | null;
  reference?: string;
  principal?: number;
  fees?: number;
  interest?: number;
  penalty?: number;
};

export const loanApi = {
  overview(token: string) {
    return authRequest<LoanOverview>("/api/loans", token);
  },
  quote(token: string, productCode: string, amount: number, frequency: string) {
    const q = new URLSearchParams({ productCode, amount: String(amount), frequency });
    return authRequest<{ quote: { principal: number; interest: number; fee: number; total: number; due: string; payments: number; instalment: number; share: number; lateDailyPercent: number; product: LoanProductView; frequency: string; termDays: number; limit: number; minAmount: number } }>(`/api/loans/quote?${q}`, token);
  },
  apply(token: string, body: Record<string, unknown>) {
    return authRequest<{ applicationId: string; ref: string; phoneMasked: string; offer: Record<string, unknown> }>("/api/loans/applications", token, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  accept(token: string, id: string, code: string) {
    return authRequest<{ outcome: "approve" | "review" | "decline"; loan?: LoanView; ref?: string; amount?: number; reasons?: LoanCheck[]; applyAgainAt?: string }>(`/api/loans/applications/${id}/accept`, token, {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },
  get(token: string, id: string) {
    return authRequest<{
      loan: LoanView;
      instalments: Array<{ seq: number; dueAt: string; total: number; paid: number; due: number; status: string; principal?: number; fee?: number; interest?: number }>;
      salesTakes: Array<{ date: string; taken: number; sales: number }>;
      activity: LoanActivityRow[];
      ledger: Array<{ type: string; amount: number; at: string; note: string | null; title?: string; balanceAfter?: number | null }>;
      lenderName: string;
      supportEmail: string;
      supportPhone: string;
    }>(`/api/loans/${id}`, token);
  },
  repay(token: string, id: string, body: { amount: number; source: "wallet" | "mpesa" }) {
    return authRequest<{ pending?: boolean; closed?: boolean; amount?: number; loan?: LoanView; message?: string }>(`/api/loans/${id}/repay`, token, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  restructure(token: string, id: string, reason: string) {
    return authRequest<{ ok: boolean }>(`/api/loans/${id}/restructure`, token, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
  acceptRestructure(token: string, id: string) {
    return authRequest<{ loan: LoanView }>(`/api/loans/${id}/restructure/accept`, token, { method: "POST" });
  },
  async document(token: string, id: string, kind: "agreement" | "statement") {
    const response = await fetch(`${apiBase()}/api/loans/${id}/document?kind=${encodeURIComponent(kind)}`, {
      headers: { Authorization: `Bearer ${token}`, Accept: "application/pdf" },
    });
    if (!response.ok) {
      let message = "Could not download that document.";
      try {
        const body = await response.json();
        if (body?.error) message = body.error;
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }
    const type = response.headers.get("content-type") || "";
    if (!type.includes("pdf")) {
      throw new Error("The server did not return a PDF. Refresh the page and try again.");
    }
    const blob = await response.blob();
    const disposition = response.headers.get("content-disposition") || "";
    const match = /filename="?([^"]+)"?/i.exec(disposition);
    return { blob, filename: match?.[1] || `jazia-loan-${kind}.pdf` };
  },
};

export type AdminLoanRow = {
  id: string;
  kind: "app" | "loan";
  ref: string;
  appRef: string | null;
  b: string;
  owner: string;
  phone: string;
  prod: string;
  code: string;
  amt: number;
  total?: number;
  repaid?: number;
  bal: number | null;
  term: string;
  band: string | null;
  score: number | null;
  dec: "Auto" | "Manual";
  st: "Pending approval" | "Declined" | "Active" | "In arrears" | "Repaid" | "Restructured" | "Written off";
  at: string;
  rev: boolean;
  reviewedBy?: string | null;
  ageH?: number | null;
  dueIn?: number | null;
  due?: string | null;
  next?: number;
  dpd?: number;
  over?: number;
  restr?: { id: string; reason: string; at: string } | null;
  offered?: { id: string; at: string; by: string | null; terms: LoanRestructureOffer | null } | null;
  flag?: string | null;
  need: number;
  appr: string[];
  checks: Array<[string, boolean, string]>;
  why: string[];
  declineReason?: string | null;
  decidedBy?: string | null;
  lastReminderAt?: string | null;
};

export type AdminLoanProduct = {
  id: string;
  code: string;
  name: string;
  v: number;
  st: "Draft" | "Awaiting approval" | "Active" | "Retired";
  loans: number;
  maker: string;
  checker: string;
  log: Array<{ at: string; text: string }>;
  desc: string;
  seg: string;
  min: number;
  max: number;
  unit: "days" | "weeks" | "months" | "years";
  tmin: number;
  tmax: number;
  tdef: number;
  method: string;
  rate: number;
  per: string;
  rmin: number;
  rmax: number;
  feeT: string;
  fee: number;
  lateT: string;
  late: number;
  cap: number;
  freq: string[];
  fdef: string;
  smin: number;
  sdef: number;
  smax: number;
  dmin: number;
  dcap: number;
  grace: number;
  defdays: number;
  early: boolean;
  wf: string;
  tier: string;
  band: string;
  hist: number;
  txn: number;
  adays: number;
  top: number;
  maxl: number;
  first: number;
  step: number;
  auto: boolean;
  autoMax: number;
  two: number;
  lender: string;
  ref: string;
  eff: string;
};

type RowResult = { row: AdminLoanRow };

export const adminLoanApi = {
  list(token: string) {
    return authRequest<{ rows: AdminLoanRow[]; products: AdminLoanProduct[]; now: string }>("/api/loans/admin", token);
  },
  counts(token: string) {
    return authRequest<{ waiting: number }>("/api/loans/admin/counts", token);
  },
  approve(token: string, id: string) {
    return authRequest<RowResult & { waiting: boolean; message: string }>(`/api/loans/admin/applications/${id}/approve`, token, { method: "POST" });
  },
  decline(token: string, id: string, reason: string) {
    return authRequest<RowResult>(`/api/loans/admin/applications/${id}/decline`, token, { method: "POST", body: JSON.stringify({ reason }) });
  },
  review(token: string, id: string) {
    return authRequest<RowResult>(`/api/loans/admin/loans/${id}/review`, token, { method: "POST" });
  },
  remind(token: string, id: string) {
    return authRequest<RowResult & { message: string }>(`/api/loans/admin/loans/${id}/remind`, token, { method: "POST" });
  },
  flag(token: string, kind: "app" | "loan", id: string, note?: string) {
    return authRequest<RowResult>(`/api/loans/admin/${kind}/${id}/flag`, token, { method: "POST", body: JSON.stringify({ note }) });
  },
  restructure(token: string, id: string, approve: boolean) {
    return authRequest<RowResult>(`/api/loans/admin/loans/${id}/restructure`, token, { method: "POST", body: JSON.stringify({ approve }) });
  },
  offerRestructure(token: string, id: string) {
    return authRequest<RowResult>(`/api/loans/admin/loans/${id}/offer-restructure`, token, { method: "POST" });
  },
  saveProduct(token: string, body: { mode: "new" | "edit" | "version" | "dup"; id?: string | undefined; submit: boolean; data: Partial<AdminLoanProduct> }) {
    return authRequest<{ product: AdminLoanProduct }>("/api/loans/admin/products", token, { method: "POST", body: JSON.stringify(body) });
  },
  approveProduct(token: string, id: string) {
    return authRequest<{ message: string }>(`/api/loans/admin/products/${id}/approve`, token, { method: "POST" });
  },
  rejectProduct(token: string, id: string, reason: string) {
    return authRequest<{ message: string }>(`/api/loans/admin/products/${id}/reject`, token, { method: "POST", body: JSON.stringify({ reason }) });
  },
  retireProduct(token: string, id: string) {
    return authRequest<{ message: string }>(`/api/loans/admin/products/${id}/retire`, token, { method: "POST" });
  },
};

export type AmlLog = { at: string; text: string; by: string };
export type AmlReason = { id: string; title: string; action: string; category: string; weight: number; detail: string };
export type AmlProfile = {
  rating: string;
  why: string[];
  withdrawalsRestricted: boolean;
  payoutsHeld: boolean;
  frozen: boolean;
  confirmedFraud: boolean;
  edd: boolean;
  log: AmlLog[];
} | null;
export type AmlLinked = { userId: string; business: string; risk: string; why: string };
export type AmlHeld = { id: string; source: string; amount: number; reason: string; status: string; reference: string; at: string; decidedBy: string };

export type AmlAlertRow = {
  id: string;
  number: string;
  category: "FRAUD" | "AML";
  title: string;
  userId: string | null;
  business: string;
  risk: string;
  amount: number | null;
  score: number;
  severity: "Critical" | "High" | "Medium" | "Low";
  status: string;
  statusLabel: string;
  rules: string[];
  expected: string | null;
  seen: string | null;
  action: string | null;
  assigned: string;
  age: string;
  createdAt: string;
  dueAt: string;
  overdue: boolean;
  ai: boolean;
  caseId: string | null;
  outcome: string;
};

export type AmlAlertDetail = AmlAlertRow & {
  reasons: AmlReason[];
  events: Array<{ id: string; type: string; amount: number | null; score: number; decision: string; at: string; rules: string }>;
  aiSummary: string;
  aiProvider: string;
  aiBasis: string;
  aiAt: string | null;
  profile: AmlProfile;
  linked: AmlLinked[];
  similar: Array<{ id: string; number: string; title: string; outcome: string; rules: string }>;
  held: AmlHeld[];
  log: AmlLog[];
};

export type AmlCaseRow = {
  id: string;
  number: string;
  userId: string | null;
  business: string;
  risk: string;
  priority: string;
  status: string;
  statusLabel: string;
  owner: string;
  openedAt: string;
  suspicionAt: string | null;
  reportDueAt: string | null;
  hoursLeft: number | null;
  rfi: string;
  outcome: string;
  alerts?: number;
};

export type AmlReportRow = {
  id: string;
  number: string;
  type: "STR" | "CTR" | "SANCTIONS" | "CBK_INCIDENT";
  typeLabel: string;
  where: string;
  caseId: string | null;
  caseNumber: string;
  userId: string | null;
  business: string;
  status: "DRAFT" | "REPORTED";
  statusLabel: string;
  amount: number | null;
  dueAt: string | null;
  hoursLeft: number | null;
  externalRef: string;
  reportedAt: string | null;
  reportedBy: string;
  narrative: string;
  aiProvider: string;
  log: AmlLog[];
  createdAt: string;
};

export type AmlCaseDetail = AmlCaseRow & {
  timeline: AmlLog[];
  alerts: AmlAlertRow[];
  reports: AmlReportRow[];
  profile: AmlProfile;
  linked: AmlLinked[];
  held: AmlHeld[];
  aiSummary: string;
  aiProvider: string;
  aiBasis: string;
};

export type AmlScreenRow = {
  id: string;
  number: string;
  userId: string;
  business: string;
  name: string;
  role: string;
  list: string;
  entryName: string;
  matchPct: number;
  status: "NEEDS_REVIEW" | "CLEARED" | "CONFIRMED";
  statusLabel: string;
  compare: Array<[string, string, string]>;
  note: string;
  decidedBy: string;
  at: string;
};

export type AmlWatchEntry = { id: string; list: string; name: string; hasPhone: boolean; idNumber: string; note: string; addedBy: string; at: string };

export type AmlSummary = {
  mode: "enforce" | "monitor";
  badges: { fraud: number; aml: number };
  fraudOpen: number;
  amlOpen: number;
  casesOpen: number;
  screeningOpen: number;
  reportsDue: number;
  reportsOverdue: number;
  pastSla: number;
  heldAmount: number;
  heldCount: number;
  measures: { alertsPer1000: number; precision: number | null; medianFirstReviewHours: number | null; reportsOnTime: number | null };
  ai: { enabled: boolean; providers: Array<{ name: string; configured: boolean; model: string }>; promptVersion: string };
};

type Msg = { message: string };
const post = (body?: unknown): RequestInit => ({ method: "POST", body: JSON.stringify(body || {}) });

export const amlApi = {
  summary: (token: string) => authRequest<AmlSummary>("/api/aml/summary", token),
  alerts: (token: string, category?: "FRAUD" | "AML") => authRequest<{ rows: AmlAlertRow[] }>(`/api/aml/alerts${category ? `?category=${category}` : ""}`, token),
  alert: (token: string, id: string) => authRequest<{ row: AmlAlertDetail }>(`/api/aml/alerts/${id}`, token),
  assign: (token: string, id: string) => authRequest<Msg>(`/api/aml/alerts/${id}/assign`, token, post()),
  closeAlert: (token: string, id: string, reason: string) => authRequest<Msg>(`/api/aml/alerts/${id}/close`, token, post({ reason })),
  confirmFraud: (token: string, id: string, note: string, significant: boolean) => authRequest<Msg>(`/api/aml/alerts/${id}/confirm-fraud`, token, post({ note, significant })),
  escalate: (token: string, id: string) => authRequest<Msg>(`/api/aml/alerts/${id}/escalate`, token, post()),
  release: (token: string, id: string, note: string) => authRequest<Msg>(`/api/aml/alerts/${id}/release`, token, post({ note })),
  openCase: (token: string, id: string) => authRequest<Msg & { caseId: string }>(`/api/aml/alerts/${id}/open-case`, token, post()),
  aiAlert: (token: string, id: string) => authRequest<Msg>(`/api/aml/alerts/${id}/ai`, token, post()),
  decideHeld: (token: string, id: string, body: { action: "release" | "match" | "return"; note: string; storeId?: string | undefined; reference?: string | undefined }) =>
    authRequest<Msg>(`/api/aml/held/${id}`, token, post(body)),

  cases: (token: string) => authRequest<{ rows: AmlCaseRow[] }>("/api/aml/cases", token),
  kase: (token: string, id: string) => authRequest<{ row: AmlCaseDetail }>(`/api/aml/cases/${id}`, token),
  requestInfo: (token: string, id: string, message: string) => authRequest<Msg>(`/api/aml/cases/${id}/request-info`, token, post({ message })),
  escalateCase: (token: string, id: string) => authRequest<Msg>(`/api/aml/cases/${id}/escalate`, token, post()),
  suspicion: (token: string, id: string, note: string) => authRequest<Msg & { reportId: string }>(`/api/aml/cases/${id}/suspicion`, token, post({ note })),
  closeCase: (token: string, id: string, reason: string) => authRequest<Msg>(`/api/aml/cases/${id}/close`, token, post({ reason })),
  aiCase: (token: string, id: string) => authRequest<Msg>(`/api/aml/cases/${id}/ai`, token, post()),
  checkMessage: (token: string, message: string) => authRequest<{ words: string[] }>("/api/aml/message-check", token, post({ message })),

  profileAction: (token: string, userId: string, action: "restrict" | "unrestrict" | "freeze" | "unfreeze" | "clear-fraud", reason: string) =>
    authRequest<Msg>(`/api/aml/customers/${userId}/${action}`, token, post({ reason })),

  screening: (token: string) => authRequest<{ rows: AmlScreenRow[] }>("/api/aml/screening", token),
  decideScreening: (token: string, id: string, decision: "clear" | "confirm", note: string) => authRequest<Msg>(`/api/aml/screening/${id}/${decision}`, token, post({ note })),
  rescreen: (token: string) => authRequest<Msg>("/api/aml/screening/run", token, post()),
  watchlist: (token: string) => authRequest<{ entries: AmlWatchEntry[]; unCount: number; unUpdated: string | null }>("/api/aml/watchlist", token),
  addWatch: (token: string, body: { list: string; name: string; phone?: string | undefined; idNumber?: string | undefined; note?: string | undefined }) =>
    authRequest<Msg>("/api/aml/watchlist", token, post(body)),
  removeWatch: (token: string, id: string) => authRequest<Msg>(`/api/aml/watchlist/${id}/remove`, token, post()),
  syncUn: (token: string) => authRequest<Msg>("/api/aml/watchlist/sync-un", token, post()),

  reports: (token: string) => authRequest<{ rows: AmlReportRow[] }>("/api/aml/reports", token),
  report: (token: string, id: string) => authRequest<{ row: AmlReportRow }>(`/api/aml/reports/${id}`, token),
  saveReport: (token: string, id: string, narrative: string) => authRequest<Msg>(`/api/aml/reports/${id}`, token, post({ narrative })),
  aiReport: (token: string, id: string) => authRequest<Msg & { narrative: string; aiProvider: string }>(`/api/aml/reports/${id}/ai`, token, post()),
  reported: (token: string, id: string, body: { externalRef: string; reportedAt: string; narrative: string }) => authRequest<Msg>(`/api/aml/reports/${id}/reported`, token, post(body)),
};

export const purchaseApi = {
  list(token: string) {
    return authRequest<{ orders: PurchaseOrderRecord[]; nextNumber: string }>("/api/purchases", token);
  },
  create(token: string, body: Record<string, unknown>) {
    return authRequest<{ order: PurchaseOrderRecord }>("/api/purchases", token, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  setStatus(token: string, orderId: string, status: "SENT") {
    return authRequest<{ order: PurchaseOrderRecord }>(`/api/purchases/${orderId}/status`, token, {
      method: "POST",
      body: JSON.stringify({ status }),
    });
  },
  receive(token: string, orderId: string, lines: Array<{ id: string; quantity: number }>) {
    return authRequest<{ order: PurchaseOrderRecord }>(`/api/purchases/${orderId}/receive`, token, {
      method: "POST",
      body: JSON.stringify({ lines }),
    });
  },
};
